import Header from 'Components/Layout/Header'
import Table from 'Components/Layout/Table'
import FormEmptyOffCanvas from 'Components/Modal/FormEmptyOffCanvas'
import Creation from 'Entity/Creation'
import CreationLocation from 'Entity/CreationLocation'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import CreationLocationTable from 'Table/Admin/Edition/CreationLocationTable'
import DeleteModal from 'Components/Modal/DeleteModal'

interface Properties {
    creation: Creation
    handleClose: () => void
}

const CreationLocationModal: React.FC<Properties> = ({
    creation,
    handleClose,
}) => {
    const { t } = useTranslation()

    const [showForm, setShowForm] = useState(false)
    const [toggleReload, setToggleReload] = useState<boolean>(true)
    const [creationLocations, setCreationLocations] = useState<
        CreationLocation[]
    >([])
    const [creationLocation, setCreationLocation] = useState<CreationLocation>({} as CreationLocation)
    const [search, setSearch] = useState<string>('')
    const [showDelete, setShowDelete] = useState<boolean>(false)

    const handleAdd = () => {
        setCreationLocation({} as CreationLocation)
        handleShow()
    }

    const deleteCreationLocation = (data: CreationLocation) => {
        setCreationLocation(data)
        setShowDelete(true)
    }

    const editCreationLocation = (data: CreationLocation) => {
        setCreationLocation(data)
        setShowForm(true)
    }

    const handleFormClose = (reload = false) => {
        setShowForm(false)

        if (reload) {
            setToggleReload(!toggleReload)
        }
    }

    const handleFormSuccess = (creationLocation: CreationLocation) => {
        setCreationLocation(creationLocation)
        setShowForm(false)
        setToggleReload(!toggleReload)
    }

    const handleShow = () => setShowForm(true)

    const handleDeleteClose = (reload = false) => {
        setCreationLocation({} as CreationLocation)
        setShowDelete(false)

        if (reload) {
            setToggleReload(!toggleReload)
        }
    }

    const actionsRender = (creationLocation: CreationLocation) => (
        <>
            {creationLocation.isGranted?.edit && (
                <Button variant="outline-primary" size="sm" onClick={() => editCreationLocation(creationLocation)}>
                    <i className="fa-solid fa-pencil me-2"></i>
                    {t('app.edit')}
                </Button>
            )}

            {creationLocation.isGranted?.delete && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteCreationLocation(creationLocation)}
                >
                    <i className="fa-solid fa-trash me-2"></i>
                    {t('app.delete')}
                </Button>
            )}
        </>
    )

    let table = CreationLocationTable

    if (table.hasOwnProperty('actions')) {
        table.actions.render = actionsRender
    }

    return (
        <div id="creationLocationModal">
            <Header
                title={creation.name}
                add={handleAdd}
                prevRotate={true}
                prev={() => handleClose()}
            />
            <Table
                path={`/studio/creation/${creation.id}/location`}
                rows={table}
                toggleReload={toggleReload}
                items={creationLocations}
                setItems={setCreationLocations}
                keyword={search}
                setKeyword={setSearch}
            />

            {showForm && (
                <FormEmptyOffCanvas
                    i18nTitle={
                        creationLocation.id
                            ? 'Edit creation location'
                            : 'Add creation location'
                    }
                    show={showForm}
                    handleClose={handleFormClose}
                    onSuccess={handleFormSuccess}
                    path={`/studio/creation/${creation.id}/location/${
                        creationLocation.id ?? 'new'
                    }`}
                    formPath={!!creationLocation.id}
                    method={creationLocation.id ? 'patch' : 'post'}
                />
            )}

            <DeleteModal
                handleClose={handleDeleteClose}
                show={showDelete}
                path={`/studio/creation/${creation.id}/location/${creationLocation?.id ?? 'error'}`}
                name={`#${creationLocation.id} - ${creationLocation.locationType}`}
                archive={true}
            />

        </div>
    )
}

export default CreationLocationModal
